import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentService } from 'src/app/services/document.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';
import { DriveItemPresentationPolicy } from './drive-item-presentation.policy';
import { DriveSelectionQuery } from './drive-selection.query';
import { DriveItem, ItemType } from './drive-viewer.models';
import { DrivePreviewState, DriveWorkspaceState } from './drive-viewer.state';

export class DrivePreviewCoordinator {
  constructor(
    private readonly workspace: DriveWorkspaceState,
    private readonly state: DrivePreviewState,
    private readonly selection: DriveSelectionQuery,
    private readonly presentation: DriveItemPresentationPolicy,
    private readonly documents: DocumentService,
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer,
    private readonly logger: FrontendLoggerService,
    private readonly translate: (key: string) => string
  ) {}

  open(item: DriveItem): void {
    if (item.type === 'folder') return;
    this.state.previewOpen = true;
    const index = this.selection.visibleItems().findIndex((candidate) => candidate.id === item.id);
    if (index >= 0) {
      this.state.previewIndex = index;
      this.state.previewItemOverride = undefined;
    } else {
      this.state.previewItemOverride = item;
      this.state.previewIndex = 0;
    }
    this.ensureItem(item);
  }

  close(): void {
    this.state.previewOpen = false;
    this.state.previewItemOverride = undefined;
    this.workspace.items.forEach((item) => this.releaseBlob(item));
  }

  previous(): void {
    if (this.state.previewIndex > 0) {
      this.state.previewItemOverride = undefined;
      this.ensureIndex(--this.state.previewIndex);
    }
  }

  next(): void {
    if (this.state.previewIndex < this.selection.visibleItems().length - 1) {
      this.state.previewItemOverride = undefined;
      this.ensureIndex(++this.state.previewIndex);
    }
  }

  isText(item: DriveItem | undefined): boolean {
    return !!item && (item.type === 'txt'
      || (item.type === 'sheet' && this.presentation.extension(item) === 'csv'));
  }

  isFrame(item: DriveItem | undefined): boolean {
    return !!item && !this.isText(item) && ['pdf', 'doc', 'sheet', 'slide'].includes(item.type);
  }

  isLoading(item: DriveItem | undefined): boolean {
    return !!item && this.state.previewLoadingIds.has(item.id);
  }

  error(item: DriveItem | undefined): string | null {
    return item ? this.state.previewErrors[item.id] ?? null : null;
  }

  safeUrl(item: DriveItem | undefined): SafeResourceUrl | null {
    return item && this.isFrame(item) ? item.previewSafeUrl ?? null : null;
  }

  text(item: DriveItem | undefined): string | null {
    return item && this.isText(item) ? item.previewText ?? null : null;
  }

  fail(item: DriveItem): void {
    this.state.previewLoadingIds.delete(item.id);
    this.state.previewErrors[item.id] = this.translate('drive.preview.not_available');
  }

  ensureIndex(index: number): void { this.ensureItem(this.selection.visibleItems()[index]); }

  ensureItem(item: DriveItem | undefined): void {
    if (!item || item.type === 'folder') return;
    if (this.isText(item) && item.previewText !== undefined) return;
    if (this.isFrame(item) && item.previewSafeUrl) return;
    if ((item.type === 'image' || item.type === 'video') && item.previewUrl) return;
    if (this.state.previewLoadingIds.has(item.id)) return;
    if (!item.documentId) {
      this.state.previewErrors[item.id] = this.translate('drive.preview.not_available');
      return;
    }
    this.resolveUrl(item);
  }

  resolveUrl(item: DriveItem): void {
    this.state.previewLoadingIds.add(item.id);
    delete this.state.previewErrors[item.id];
    this.documents.getDocumentPublicUrl(item.documentId!).subscribe({
      next: (publicUrl) => this.loadResolvedContent(item, publicUrl),
      error: (error) => {
        this.logger.error(`[DriveViewerComponent] Error al resolver URL pública para previsualización: ${JSON.stringify(error)}`);
        this.fail(item);
      }
    });
  }

  fetchTextContent(url: string, item: DriveItem): void {
    this.http.get(url, { responseType: 'text' }).subscribe({
      next: (text) => {
        const target = this.target(item);
        if (target) target.previewText = text;
        this.state.previewLoadingIds.delete(item.id);
      },
      error: (error) => {
        this.logger.error(`[DriveViewerComponent] No se pudo cargar el texto para previsualización: ${JSON.stringify(error)}`);
        this.fail(item);
      }
    });
  }

  fetchPdfBlob(url: string, item: DriveItem): void {
    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => this.applyPdfBlob(item, blob),
      error: (error) => {
        this.logger.error(`[DriveViewerComponent] No se pudo cargar el PDF para previsualización (posible CORS): ${JSON.stringify(error)}`);
        const target = this.target(item);
        if (target) {
          target.previewUrl = url;
          target.previewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        }
        this.state.previewLoadingIds.delete(item.id);
      }
    });
  }

  buildViewerUrl(item: DriveItem, rawUrl: string): string { return this.presentation.viewerUrl(item, rawUrl); }
  extension(item: DriveItem): string | undefined { return this.presentation.extension(item) || undefined; }
  fileIcon(filename: string): string { return this.presentation.fileIcon(filename); }
  iconClass(filename: string): string { return this.presentation.iconClass(filename); }
  mapType(filename: string): ItemType { return this.presentation.typeByExtension(filename); }
  itemIcon(item: DriveItem): string { return this.presentation.itemIcon(item); }
  itemIconClass(item: DriveItem): string { return this.presentation.itemIconClass(item); }

  destroy(): void { this.workspace.items.forEach((item) => this.releaseBlob(item)); }

  private loadResolvedContent(item: DriveItem, publicUrl: string): void {
    this.logger.info(`[DriveViewerComponent] URL pública resuelta para previsualización: ${publicUrl}`);
    const extension = this.presentation.extension(item);
    if (item.type === 'txt' || (item.type === 'sheet' && extension === 'csv')) {
      this.loadTextThroughBackend(item, publicUrl);
    } else if (item.type === 'pdf') {
      this.loadPdfThroughBackend(item, publicUrl);
    } else {
      const target = this.target(item);
      const url = this.presentation.viewerUrl(item, publicUrl);
      if (target) {
        target.previewUrl = url;
        target.previewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      }
      this.state.previewLoadingIds.delete(item.id);
    }
  }

  private loadTextThroughBackend(item: DriveItem, publicUrl: string): void {
    this.documents.getDocumentContentText(item.documentId!).subscribe({
      next: (text) => {
        const target = this.target(item);
        if (target) target.previewText = text;
        this.state.previewLoadingIds.delete(item.id);
      },
      error: (error) => {
        this.logger.error(`[DriveViewerComponent] No se pudo cargar texto vía backend; fallback: ${JSON.stringify(error)}`);
        this.fetchTextContent(publicUrl, item);
      }
    });
  }

  private loadPdfThroughBackend(item: DriveItem, publicUrl: string): void {
    this.documents.getDocumentContentBlob(item.documentId!).subscribe({
      next: (blob) => this.applyPdfBlob(item, blob),
      error: (error) => {
        this.logger.error(`[DriveViewerComponent] No se pudo cargar PDF vía backend; fallback: ${JSON.stringify(error)}`);
        this.fetchPdfBlob(publicUrl, item);
      }
    });
  }

  private applyPdfBlob(item: DriveItem, blob: Blob): void {
    const objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    const target = this.target(item);
    if (target) {
      this.releaseBlob(target);
      target.previewUrl = objectUrl;
      target.previewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
    }
    this.state.previewLoadingIds.delete(item.id);
  }

  private target(item: DriveItem): DriveItem | undefined {
    return this.workspace.items.find((candidate) => candidate.id === item.id);
  }

  private releaseBlob(item: DriveItem): void {
    if (item.previewUrl?.startsWith('blob:')) {
      try { URL.revokeObjectURL(item.previewUrl); } catch { /* already revoked */ }
      item.previewUrl = undefined;
      item.previewSafeUrl = undefined;
    }
  }
}
